/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import * as Icons from './icons/Icons';
import DisplayHtmlContent from './DisplayHtmlContent';
import Heading from './Heading';
import Checkbox from './Checkbox';

export default class NeedToKnow extends React.Component {
    static propTypes = {
        /** Text to be used for the heading */
        headerText: PropTypes.string,

        /** The heading level (h2, h3, etc) to use for the heading */
        headingLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),

        /** Text used for the box between the heading and the content block */
        introText: PropTypes.string,

        /** The name of the site, used in the default intro text. Not used if a
         * value for introText is provided */
        name: PropTypes.string,

        /** A string (can be HTML) that will be rendered into the content area */
        siteInfo: PropTypes.string,

        /** An array of strings (can be HTML) to be rendered into an unordered
         * list in the content area after siteInfo */
        listItems: PropTypes.array,

        /** Children are rendered into the content area after siteInfo and listItems */
        children: PropTypes.node,

        /** Label for the confirmation checkbox */
        checkboxText: PropTypes.string,

        /** Id for the checkbox input */
        checkboxId: PropTypes.string,

        /** If the checkbox is checked */
        isChecked: PropTypes.bool,

        /** Change handler for the checkbox */
        handleChange: PropTypes.func,

        /** Makes the checkbox visible */
        showCheckbox: PropTypes.bool,

        /** Additional class name added to the outer container */
        className: PropTypes.string,

        /** Additional class name added to the outer container */
        isRequired: PropTypes.bool,

        /** Marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,
    };

    static defaultProps = {
        checkboxId: 'need-to-know-checkbox',
        checkboxText: 'Yes, I have read and agree to the Need to Know information.',
        children: undefined,
        className: undefined,
        handleChange: undefined,
        headerText: 'Need to Know',
        headingLevel: 2,
        introText: undefined,
        isChecked: false,
        name: 'our site',
        showCheckbox: true,
        siteInfo: undefined,
        isRequired: true,
        isInvalid: false,
        errorText: 'Please read and agree to the important Need to Know information.',
    };

    state = {
        // Supporting the `checked` prop for legacy usage
        // eslint-disable-next-line react/prop-types
        consentBoxChecked: this.props.isChecked || !!this.props.checked,
        showIndicator: true,
        isInvalid: this.props.isInvalid,
    };

    componentDidMount() {
        const ref = this.informationContentRef;
        if (ref && ref.scrollHeight < 320) {
            // because informationContentRef is max-height: 32rem;
            ref.parentElement.classList.add(
                `${ref.parentElement.classList}`,
                'no-scrollable-content'
            );
        }
    }

    componentDidUpdate({ checked, isChecked, isInvalid }) {
        // Pull in changes to the checked state (both legacy [checked] and standard [isChecked]).
        // `setState` is OK because we've wrapped it in a prop conditional.
        if (typeof this.props.isChecked !== 'undefined') {
            if (isChecked !== this.props.isChecked) {
                this.setState({ consentBoxChecked: this.props.isChecked });
            }
        } else if (typeof this.props.checked !== 'undefined') {
            if (checked !== this.props.checked) {
                this.setState({ consentBoxChecked: this.props.checked });
            }
        }

        if (this.props.isInvalid !== isInvalid) {
            this.setState({ isInvalid: this.props.isInvalid });
        }
    }

    getInfo = () => {
        // return site information if it exists, maintaining any HTML it contains
        const { siteInfo } = this.props;

        if (siteInfo) {
            return <DisplayHtmlContent html={siteInfo} />;
        }

        return null;
    };

    handleScroll = (e) => {
        // Hide the scroll indicator once the user scrolls down 100px
        const bottom = e.target.scrollTop > 100;
        this.setState({ showIndicator: !bottom });
    };

    createList = () => {
        const { listItems } = this.props;

        if (listItems) {
            const list = listItems.map((item, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>
                    <DisplayHtmlContent html={item.notice_text} />
                </li>
            ));

            return <ul>{list}</ul>;
        }

        return null;
    };

    toggleCheckBox = () => {
        // toggle status of the checkbox
        if (typeof this.props.handleChange === 'function') {
            this.props.handleChange();
        }
        this.setState({
            consentBoxChecked: !this.state.consentBoxChecked,
        });
    };

    validate = () => {
        const { checkboxId, errorText } = this.props;

        const validationObject = {
            isInvalid: false,
            errorText: null,
            id: checkboxId,
        };

        if (!this.state.consentBoxChecked) {
            validationObject.isInvalid = true;
            validationObject.errorText = errorText;

            this.setState({
                isInvalid: true,
            });
        } else {
            this.setState({
                isInvalid: false,
            });
        }

        return validationObject;
    };

    render() {
        /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
        const {
            children,
            className,
            name,
            showCheckbox,
            checkboxId,
            checkboxText,
            headerText,
            headingLevel,
            isRequired,
            introText = `Below you will find important information that regards your stay with
                us. Please carefully read the information and click the check box below
                to agree to our Rules and Regulations for ${name}.`,
            errorText,
        } = this.props;

        const checkboxLabel = isRequired ? (
            <React.Fragment>
                {checkboxText}
                <abbr className="rec-label-required-indicator">
                    <span aria-hidden="true">*</span>
                    <span className="rec-sr-only"> (Required) </span>
                </abbr>
            </React.Fragment>
        ) : (
            checkboxText
        );

        return (
            <section
                className={cx('rec-order-detail-need-to-know', 'sarsa-need-to-know', {
                    [className]: className,
                })}
            >
                <Heading
                    className="rec-section-header"
                    appearance="h4"
                    headingLevel={headingLevel}
                >
                    {headerText}
                </Heading>

                <div className="rec-notification-warning">
                    <span className="rec-notification-icon">
                        <Icons.IconWarning className="rec-icon-warning" />
                    </span>
                    <div className="rec-notification-body">
                        <div className="rec-notification-title">
                            Important information
                        </div>
                        <p>{introText}</p>
                    </div>
                </div>

                <div className="information">
                    <div
                        tabIndex="0"
                        className="information-content"
                        ref={(node) => {
                            this.informationContentRef = node;
                        }}
                        onScroll={this.handleScroll}
                    >
                        {!children && (
                            <div className="rec-information-head">
                                Facility Information
                            </div>
                        )}
                        {this.getInfo()}
                        {this.createList()}
                        {children}
                    </div>
                    <div
                        className={`information-scroll-indicator ${
                            this.state.showIndicator ? '' : 'hide-indicator'
                        }`}
                        aria-hidden="true"
                    >
                        <div className="information-scroll-pill">Scroll to Read More</div>
                    </div>
                </div>

                <div
                    className={cx('rec-form-check-wrap', {
                        'rec-form-no-checkbox': !showCheckbox,
                        'is-invalid':
                            this.state.isInvalid && !this.state.consentBoxChecked,
                    })}
                >
                    {showCheckbox && (
                        <Checkbox
                            label={checkboxLabel}
                            id={checkboxId}
                            value={checkboxId}
                            isRequired={isRequired}
                            isChecked={this.state.consentBoxChecked}
                            onChange={this.toggleCheckBox}
                            aria-describedby={
                                this.state.isInvalid ? `${checkboxId}-error` : null
                            }
                        />
                    )}
                </div>
                {this.state.isInvalid && !this.state.consentBoxChecked && (
                    <div id={`${checkboxId}-error`} className="sarsa-field-error-message">
                        <span className="req" aria-hidden="true">
                            *
                        </span>
                        {errorText}
                    </div>
                )}
            </section>
        );
    }
}
